import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'https://api.conectachurch.com.br';
const PUB = process.env.PUBLIC_URL || '';
const ASSET = (f) => `${PUB}/conectachurch-landing/${f}`;

// ===== Dados estruturados (JSON-LD) para SEO / LLMs =====
const SITE_URL = 'https://conectachurch.com.br/';
const SITE_LOGO = 'https://conectachurch.com.br/images/logo_conecta_church_white_V2.png';

const SITE_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'Smyrnacore Sementes Digitais',
      alternateName: 'ConectaChurch',
      url: SITE_URL,
      logo: SITE_LOGO,
      sameAs: ['https://conectachurch.com.br/'],
    },
    {
      '@type': 'WebSite',
      name: 'ConectaChurch',
      url: SITE_URL,
      description:
        'Plataforma completa de gestão eclesiástica: consolidação, secretaria, cursos, discipulado, grupos de crescimento, voluntariado e finanças.',
      inLanguage: 'pt-BR',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'ConectaChurch',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '49',
        priceCurrency: 'BRL',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'O que é a consolidação no ConectaChurch?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'O módulo de consolidação recebe automaticamente os visitantes e os distribui para os consolidadores, registrando cada etapa da jornada (visita, retorno, decisão) para que ninguém seja esquecido.',
          },
        },
        {
          '@type': 'Question',
          name: 'Como a secretaria se organiza na plataforma?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A secretaria reúne em um só lugar o cadastro e a importação de membros, a gestão de ministérios e voluntariado, e os relatórios prontos para a reunião pastoral.',
          },
        },
        {
          '@type': 'Question',
          name: 'O ConectaChurch ajuda na gestão financeira da igreja?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. O módulo financeiro registra entradas e saídas, classifica receitas e despesas por ministério e gera o balancete mensal com transparência.',
          },
        },
        {
          '@type': 'Question',
          name: 'É possível oferecer cursos e treinamentos?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sim. Há gestão completa de cursos com matrículas, frequência, instrutores e portal do aluno, além de discipulado e grupos de crescimento integrados.',
          },
        },
      ],
    },
  ],
};

// Identidade (o CSS da landing usa estas variáveis)
const LIME = '#00F1A7';
const TEXT_LIGHT = '#E9F1EF';

// ===== Conteúdo real preservado do site atual (ConectaChurch) =====
const NAV_LINKS = [
  { label: 'Produtos', href: '#plataforma' },
  { label: 'Como funciona', href: '#como-funciona' },
  { label: 'Para igrejas', href: '#planos' },
  { label: 'Contato', href: '#contato' },
];

const FEATURES = [
  {
    icon: 'ok',
    title: 'Conciliação inteligente',
    desc: 'Distribua visitantes automaticamente para os consolidadores e acompanhe cada etapa da jornada do novo membro em tempo real. Chega de planilhas e confusão.',
  },
  {
    icon: 'marker',
    title: 'Jornada do membro',
    desc: 'Visualize o caminho completo: visitante → consolidação → discipulado → grupo de crescimento → ministério. Do primeiro contato ao discipulador.',
  },
  {
    icon: 'grid',
    title: 'Células de crescimento',
    desc: 'Gestão de grupos de crescimento com responsáveis, participantes e acompanhamento pastoral. Nenhuma pessoa fica para trás.',
  },
  {
    icon: 'chart',
    title: 'Relatórios inteligentes',
    desc: 'Frequência, consolidação, discipulado, formandos e voluntariado — tudo em dashboards prontos para apresentar ao pastor.',
  },
  {
    icon: 'chat',
    title: 'Comunicação inteligente',
    desc: 'Notificações por perfil e acompanhamento de visitantes e membros em tempo real, com alertas para quem precisa de atenção.',
  },
  {
    icon: 'card',
    title: 'Finanças e dízimos',
    desc: 'Entradas e saídas, classificador de receitas e despesas por ministério e balancete mensal com transparência total.',
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Cadastre sua membresia',
    desc: 'Importação rápida via planilha ou cadastro direto. Todos os dados organizados em um só lugar.',
  },
  {
    num: '02',
    title: 'Distribua suas frentes',
    desc: 'O sistema distribui visitantes para consolidadores, grupos e ministérios, notificando cada responsável automaticamente.',
  },
  {
    num: '03',
    title: 'Acompanhe os resultados',
    desc: 'Veja o progresso de cada pessoa, registre visitas, avance no discipulado e acompanhe os indicadores da sua igreja.',
  },
];

const CHURCH_BENEFITS = [
  'Gestão completa de visitantes e membros',
  'Consolidação e discipulado rastreados',
  'Frequência e matrícula de cursos',
  'Grupos de crescimento e voluntariado',
  'Relatórios prontos para a reunião pastoral',
  'Multi-perfis: pastor, secretaria, professor, consolidador',
];

const TESTIMONIALS = [
  {
    quote:
      'Antes do ConectaChurch, perdíamos membros de vista porque a consolidação era no papel. Agora consigo ver no painel quem está sendo acompanhado e quem precisa de atenção. Isso mudou o nosso ministério.',
    name: 'Pastor Rafael Mendes',
    role: 'Paz da Igreja — 750 membros',
  },
  {
    quote:
      'A jornada do membro nos deu visão de onde cada pessoa está — da primeira visita ao discipulado. Os relatórios prontos economizam horas da secretaria.',
    name: 'Pastor Rafael Mendes',
    role: 'Paz da Igreja — 750 membros',
  },
];

const PLANS = [
  {
    id: 'gratis',
    tag: 'Para começar',
    title: 'Grátis',
    price: 'R$ 0',
    suffix: '/mês',
    desc: 'Para igrejas que estão começando a organizar a membresia.',
    features: ['Até 100 membros', '1 ministério', 'Relatórios básicos'],
    cta: 'Começar agora',
    featured: false,
  },
  {
    id: 'igreja',
    tag: 'Mais popular',
    title: 'Igreja',
    price: 'R$ 49',
    suffix: '/mês',
    desc: 'Tudo o que sua comunidade precisa para crescer com ordem.',
    features: ['Membros ilimitados', 'Todos os ministérios', 'Células e relatórios', 'Comunicação'],
    cta: 'Começar agora',
    featured: true,
  },
  {
    id: 'rede',
    tag: 'Para redes',
    title: 'Rede',
    price: 'R$ 99',
    suffix: '/mês',
    desc: 'Para redes e denominações com múltiplas igrejas.',
    features: ['Tudo do plano Igreja', 'Múltiplas igrejas', 'Suporte dedicado'],
    cta: 'Começar agora',
    featured: false,
  },
];

// ===== Planos dinâmicos (API /saas/planos-publicos) =====
const MODULO_LABELS = {
  secretaria: 'Secretaria',
  consolidacao: 'Consolidação',
  start: 'Início',
  cursos: 'Cursos',
  discipulado: 'Discipulado',
  grupos_crescimento: 'Grupos de crescimento',
  jornada: 'Jornada do membro',
  voluntariado: 'Voluntariado',
  notificacoes: 'Notificações',
};

const formatPrice = (cents) => {
  if (!cents) return 'R$ 0';
  const reais = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `R$ ${reais}`;
};

const planTag = (plano) => {
  if (plano.slug === 'igreja') return 'Mais popular';
  if (!plano.preco_mensal) return 'Para começar';
  if (!plano.max_membros) return 'Para redes';
  return 'Para sua igreja';
};

const planModulos = (plano) => {
  const mods = Array.isArray(plano.modulos) ? plano.modulos.map((m) => MODULO_LABELS[m] || m) : [];
  return plano.max_membros ? [...mods, `Até ${plano.max_membros} membros`] : [...mods, 'Membros ilimitados'];
};

// ============ CSS (fiel ao layout aprovado) ============
const LANDING_CSS = `
:root {
  --bg: #05080F;
  --bg-2: #071219;
  --surface: #0A141C;
  --surface-2: #0D1B24;
  --border: rgba(0, 241, 167, 0.10);
  --border-strong: rgba(0, 241, 167, 0.22);
  --text: #E9F1EF;
  --muted: #93A7A3;
  --teal: #00F1A7;
  --teal-soft: rgba(0, 241, 167, 0.14);
  --lime: #B4F300;
  --lime-2: #CDFF3D;
  --radius: 5px;
  --radius-sm: 5px;
  --container: 1180px;
  --font-head: "Plus Jakarta Sans", system-ui, sans-serif;
  --font-body: "Inter", system-ui, sans-serif;
}

* { margin: 0; padding: 0; box-sizing: border-box; }
html { scroll-behavior: smooth; }

body {
  font-family: var(--font-body);
  background: var(--bg);
  color: var(--text);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
}

h1, h2, h3, h4 { font-family: var(--font-head); line-height: 1.12; letter-spacing: -0.02em; }

a { color: inherit; text-decoration: none; }
img { display: block; max-width: 100%; }
ul { list-style: none; }

.container { width: min(var(--container), 92%); margin: 0 auto; }

.eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--font-head); font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.22em; text-transform: uppercase; color: var(--teal);
  margin-bottom: 18px;
}
.eyebrow::before { content: ""; width: 7px; height: 7px; background: var(--teal); border-radius: 50%; box-shadow: 0 0 12px var(--teal); }

.section { padding: 96px 0; position: relative; }

.btn {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--font-head); font-weight: 700; font-size: 0.92rem;
  padding: 12px 24px; border-radius: 5px; cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  border: 1px solid transparent;
}
.btn:hover { transform: translateY(-2px); }
.btn .arr { transition: transform 0.15s ease; }
.btn:hover .arr { transform: translateX(3px); }
.btn-lime { background: var(--lime); color: #0B1200; box-shadow: 0 8px 26px rgba(180, 243, 0, 0.22); }
.btn-lime:hover { background: var(--lime-2); }
.btn-ghost { background: transparent; color: var(--text); border-color: var(--border-strong); }
.btn-ghost:hover { border-color: var(--teal); color: var(--teal); }

/* ============ HEADER ============ */
header {
  position: sticky; top: 0; z-index: 50;
  background: rgba(5, 8, 15, 0.72);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.nav { display: flex; align-items: center; justify-content: space-between; height: 74px; }
.brand { display: flex; align-items: center; }
.brand img { height: 52px; width: auto; }
.nav-links { display: flex; align-items: center; gap: 34px; }
.nav-links a { font-size: 0.92rem; color: var(--muted); font-weight: 500; transition: color 0.15s ease; }
.nav-links a:hover { color: var(--text); }
.nav-cta { display: flex; align-items: center; gap: 12px; }
.burger { display: none; background: none; border: none; cursor: pointer; }
.burger span { display: block; width: 22px; height: 2px; background: var(--text); margin: 5px 0; }

/* ============ HERO ============ */
.hero { padding: 72px 0 40px; position: relative; overflow: hidden; }
.hero-glow {
  position: absolute; inset: 0; z-index: 0; pointer-events: none;
  background:
    radial-gradient(680px 420px at 82% 6%, rgba(0, 241, 167, 0.18), transparent 62%),
    radial-gradient(560px 360px at 8% 30%, rgba(180, 243, 0, 0.08), transparent 60%),
    radial-gradient(700px 500px at 60% 110%, rgba(0, 241, 167, 0.10), transparent 62%);
}
.hero-video {
  position: absolute; inset: 0; z-index: 0; opacity: 0.20; pointer-events: none;
  object-fit: cover; width: 100%; height: 100%;
}
.hero .container { position: relative; z-index: 2; }
.hero-grid { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 56px; align-items: center; }

.hero h1 { font-size: clamp(1.9rem, 5vw, 4.4rem); font-weight: 800; margin-bottom: 22px; overflow-wrap: break-word; }
.hero h1 .t { color: var(--teal); text-shadow: 0 0 32px rgba(0, 241, 167, 0.35); }
.hero p.sub { color: var(--muted); font-size: 1.12rem; max-width: 480px; margin-bottom: 32px; }
.hero-actions { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 36px; }
.social { display: flex; align-items: center; gap: 16px; }
.avatars { display: flex; }
.avatars img {
  width: 42px; height: 42px; border-radius: 50%; border: 2px solid var(--bg);
  margin-left: -10px; object-fit: cover;
}
.avatars img:first-child { margin-left: 0; }
.social small { color: var(--muted); font-size: 0.88rem; max-width: 300px; line-height: 1.4; }

/* stats network */
.stats-network { position: relative; padding: 10px; }
.stats-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
.stats-grid {
  position: relative; display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
}
.stat {
  background: linear-gradient(160deg, var(--surface-2), var(--surface));
  border: 1px solid var(--border); border-radius: var(--radius);
  padding: 22px; position: relative; overflow: hidden;
}
.stat .val { font-family: var(--font-head); font-size: 2.2rem; font-weight: 800; }
.stat .val span { color: var(--teal); }
.stat .lbl { color: var(--muted); font-size: 0.85rem; margin-top: 4px; }
.stat .ic {
  position: absolute; top: 16px; right: 16px; width: 34px; height: 34px;
  display: grid; place-items: center; border-radius: 5px;
  background: var(--teal-soft); color: var(--teal);
}

/* ============ DASHBOARD MOCKUP ============ */
.dash-wrap { margin-top: 56px; position: relative; }
.dash-glow { position: absolute; inset: -30px; z-index: 0; background: radial-gradient(600px 300px at 50% 30%, rgba(0, 241, 167, 0.12), transparent 60%); filter: blur(10px); }
.dash {
  position: relative; z-index: 1;
  display: grid; grid-template-columns: 220px 1fr; gap: 0;
  background: #08121A; border: 1px solid var(--border-strong);
  border-radius: 5px; overflow: hidden; box-shadow: 0 40px 90px rgba(0, 0, 0, 0.6);
}
.dash-side { background: #06101A; border-right: 1px solid rgba(255, 255, 255, 0.05); padding: 20px 16px; }
.dash-side .ds-head { display: flex; align-items: center; gap: 9px; font-family: var(--font-head); font-weight: 700; padding: 6px 8px 18px; }
.dash-side .ds-head img { height: 20px; }
.dash-side .ds-head .dot { width: 26px; height: 26px; border-radius: 5px; background: var(--teal-soft); display: grid; place-items: center; }
.ds-item {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  border-radius: 5px; color: var(--muted); font-size: 0.88rem; margin-bottom: 3px;
}
.ds-item svg { width: 17px; height: 17px; }
.ds-item.active { background: var(--teal-soft); color: var(--teal); font-weight: 600; }

.dash-main { padding: 22px 24px; }
.ds-title { font-family: var(--font-head); font-weight: 700; font-size: 1.1rem; margin-bottom: 18px; }
.ds-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 18px; }
.ds-kpi {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm);
  padding: 16px 18px;
}
.ds-kpi .l { color: var(--muted); font-size: 0.8rem; }
.ds-kpi .v { font-family: var(--font-head); font-size: 1.7rem; font-weight: 800; margin: 4px 0 6px; }
.ds-kpi .d { font-size: 0.72rem; }
.ds-kpi .d.up { color: var(--teal); }
.ds-kpi .d.lime { color: var(--lime); }

.ds-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.ds-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 16px; }
.ds-card h4 { font-size: 0.95rem; margin-bottom: 12px; }
.ds-member { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-top: 1px solid rgba(255,255,255,0.04); }
.ds-member:first-child { border-top: 0; }
.ds-member img { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.ds-member .n { font-size: 0.85rem; font-weight: 600; }
.ds-member .r { font-size: 0.72rem; color: var(--muted); }
.ds-chart { display: flex; align-items: flex-end; gap: 8px; height: 130px; padding-top: 8px; }
.bar { flex: 1; background: linear-gradient(180deg, var(--teal), rgba(0, 241, 167, 0.15)); border-radius: 5px 5px 0 0; }

/* ============ FEATURES ============ */
.features { background: var(--bg-2); }
.sec-head { max-width: 720px; margin: 0 auto 56px; text-align: center; }
.sec-head h2 { font-size: clamp(1.9rem, 3.4vw, 2.9rem); font-weight: 800; }
.sec-head p { color: var(--muted); margin-top: 16px; }
.feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.feat {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 28px; transition: border-color 0.2s ease, transform 0.2s ease;
}
.feat:hover { border-color: var(--teal); transform: translateY(-4px); }
.feat .fic {
  width: 48px; height: 48px; border-radius: 5px; display: grid; place-items: center;
  background: var(--teal-soft); color: var(--teal); margin-bottom: 20px;
}
.feat h3 { font-size: 1.18rem; margin-bottom: 10px; }
.feat p { color: var(--muted); font-size: 0.92rem; margin-bottom: 18px; }
.feat .more { color: var(--teal); font-weight: 600; font-size: 0.88rem; display: inline-flex; align-items: center; gap: 6px; }

/* ============ STEPS ============ */
.steps-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 26px; position: relative; }
.step { text-align: center; padding: 0 8px; position: relative; }
.step .num {
  width: 74px; height: 74px; margin: 0 auto 18px; border-radius: 50%;
  display: grid; place-items: center; font-family: var(--font-head); font-weight: 800; font-size: 1.5rem;
  background: var(--surface); border: 1px solid var(--border-strong); color: var(--teal);
  box-shadow: 0 0 30px rgba(0, 241, 167, 0.12);
}
.step h3 { font-size: 1.2rem; margin-bottom: 10px; }
.step p { color: var(--muted); font-size: 0.92rem; }
.steps-arrow {
  position: absolute; top: 37px; color: var(--teal); opacity: 0.5;
}
.steps-arrow.s1 { left: 33%; }
.steps-arrow.s2 { left: 66%; }
.steps-foot { text-align: center; margin-top: 44px; }

/* ============ TESTIMONIAL ============ */
.testimonial { background: var(--bg-2); }
.testi-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
.testi-copy .checks { margin-top: 26px; display: grid; gap: 14px; }
.checks li { display: flex; align-items: center; gap: 12px; color: var(--muted); font-size: 0.96rem; }
.checks li svg { color: var(--teal); flex-shrink: 0; }
.quote-card {
  background: linear-gradient(160deg, var(--surface-2), var(--surface));
  border: 1px solid var(--border-strong); border-radius: var(--radius);
  padding: 34px; position: relative;
}
.quote-card .qmark { font-family: var(--font-head); font-size: 4rem; color: var(--teal); line-height: 1; margin-bottom: 6px; }
.quote-card blockquote { font-size: 1.15rem; color: var(--text); margin-bottom: 26px; }
.quote-person { display: flex; align-items: center; gap: 14px; }
.quote-person img { width: 52px; height: 52px; border-radius: 50%; object-fit: cover; }
.quote-person .n { font-family: var(--font-head); font-weight: 700; }
.quote-person .r { color: var(--muted); font-size: 0.85rem; }
.quote-nav { display: flex; gap: 10px; margin-top: 26px; }
.qbtn { width: 42px; height: 42px; border-radius: 50%; border: 1px solid var(--border-strong); background: transparent; color: var(--text); display: grid; place-items: center; cursor: pointer; }
.qbtn:hover { border-color: var(--teal); color: var(--teal); }

/* ============ PRICING ============ */
.price-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; align-items: stretch; }
.plan {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 30px; display: flex; flex-direction: column;
}
.plan.featured { border-color: var(--teal); box-shadow: 0 0 50px rgba(0, 241, 167, 0.10); position: relative; }
.plan .tag { font-size: 0.78rem; color: var(--teal); font-weight: 600; margin-bottom: 6px; }
.plan h3 { font-size: 1.25rem; }
.plan .price { font-family: var(--font-head); font-weight: 800; font-size: 2.4rem; margin: 14px 0 4px; }
.plan .price small { font-size: 0.9rem; color: var(--muted); font-weight: 500; }
.plan p.desc { color: var(--muted); font-size: 0.88rem; margin-bottom: 22px; }
.plan ul { display: grid; gap: 12px; margin-bottom: 26px; }
.plan ul li { display: flex; align-items: center; gap: 10px; color: var(--muted); font-size: 0.9rem; }
.plan ul li svg { color: var(--teal); flex-shrink: 0; }
.plan .btn { justify-content: center; margin-top: auto; }
.plan .trial { margin-top: 14px; text-align: center; color: var(--muted); font-size: 0.78rem; }

/* ============ CTA BAND ============ */
.cta-band { position: relative; overflow: hidden; border-radius: 5px; background: linear-gradient(150deg, #0A1D26, #05080F); border: 1px solid var(--border-strong); padding: 56px; }
.cta-band::before { content: ""; position: absolute; inset: 0; background: radial-gradient(540px 300px at 80% 50%, rgba(0, 241, 167, 0.20), transparent 60%); }
.cta-inner { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
.cta-inner h2 { font-size: clamp(1.8rem, 3vw, 2.6rem); font-weight: 800; max-width: 540px; }
.cta-inner h2 span { color: var(--teal); }
.cta-inner .sub { color: var(--muted); margin: 12px 0 24px; max-width: 460px; }
.cta-actions { display: flex; gap: 12px; flex-wrap: wrap; }

/* ============ FOOTER ============ */
footer { border-top: 1px solid rgba(255, 255, 255, 0.06); padding: 60px 0 32px; }
.foot-grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr; gap: 32px; }
.foot-brand p { color: var(--muted); font-size: 0.9rem; margin-top: 16px; max-width: 300px; }
.foot-col h4 { font-size: 0.82rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text); margin-bottom: 18px; }
.foot-col ul { display: grid; gap: 11px; }
.foot-col a { color: var(--muted); font-size: 0.9rem; }
.foot-col a:hover { color: var(--teal); }
.foot-bottom { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; margin-top: 48px; padding-top: 26px; border-top: 1px solid rgba(255, 255, 255, 0.06); }
.foot-bottom small { color: var(--muted); }

/* ============ RESPONSIVE ============ */
@media (max-width: 980px) {
  .hero-grid, .testi-grid { grid-template-columns: 1fr; gap: 40px; }
  .feat-grid, .steps-grid, .price-grid { grid-template-columns: 1fr; }
  .steps-arrow { display: none; }
  .dash { grid-template-columns: 1fr; }
  .dash-side { display: none; }
  .ds-cols { grid-template-columns: 1fr; }
  .foot-grid { grid-template-columns: 1fr 1fr; }
  .steps-grid, .price-grid { gap: 18px; }
}
@media (max-width: 640px) {
  .nav-links, .nav-cta .btn { display: none; }
  .burger { display: block; }
  .stats-grid { grid-template-columns: 1fr; }
  .ds-kpis { grid-template-columns: 1fr 1fr; }
  .foot-grid { grid-template-columns: 1fr; }
  .section { padding: 64px 0; }
  .cta-band { padding: 36px 24px; }
  .hero { padding: 48px 0 24px; }
}
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); border: 0; }
`;

const ICON_PATHS = {
  ok: 'M8 12l3 3 5-6',
  marker: 'M12 21s-7-5-7-11a7 7 0 0114 0c0 6-7 11-7 11z',
  grid: 'M4 4h16v16H4z M4 10h16 M10 10v10',
  chart: 'M4 19h16M7 16l3-4 3 2 4-6',
  chat: 'M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z',
  card: 'M3 6h18v14H3z M3 10h18 M8 3v4 M16 3v4',
};

function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ nome: '', igreja: '', email: '', telefone: '', mensagem: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/contato`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); setForm({ nome: '', igreja: '', email: '', telefone: '', mensagem: '' }); onClose(); }, 2000);
    } catch {
      // falha silenciosa, mantém o modal
    }
    setLoading(false);
  };

  const fieldSx = {
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
    '& .MuiOutlinedInput-root': { color: TEXT_LIGHT, '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' } },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, bgcolor: '#0A141C', color: TEXT_LIGHT } }}>
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: TEXT_LIGHT }}>
            Agendar demonstração
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: TEXT_LIGHT }}><CloseIcon /></IconButton>
        </Box>
        {success ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Mensagem enviada! Entraremos em contato em breve.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Seu nome" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} required size="small" sx={fieldSx} />
            <TextField label="Nome da igreja" value={form.igreja} onChange={(e) => setForm((f) => ({ ...f, igreja: e.target.value }))} required size="small" sx={fieldSx} />
            <TextField label="E-mail" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required size="small" sx={fieldSx} />
            <TextField label="Telefone / WhatsApp" value={form.telefone} onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))} size="small" sx={fieldSx} />
            <TextField label="Como podemos ajudar?" value={form.mensagem} onChange={(e) => setForm((f) => ({ ...f, mensagem: e.target.value }))} multiline rows={3} size="small" sx={fieldSx} />
            <Button type="submit" variant="contained" disabled={loading} sx={{ bgcolor: LIME, color: '#0B1200', fontWeight: 700, py: 1.5, '&:hover': { bgcolor: '#CDFF3D' } }}>
              {loading ? <CircularProgress size={20} /> : 'Enviar mensagem'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [contactModal, setContactModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [planos, setPlanos] = useState([]);

  useEffect(() => {
    const id = window.setInterval(() => setQuoteIdx((i) => (i + 1) % TESTIMONIALS.length), 7000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/saas/planos-publicos`)
      .then((r) => r.json())
      .then((d) => { if (alive) setPlanos(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setPlanos([]); });
    return () => { alive = false; };
  }, []);

  // Injeta os dados estruturados (JSON-LD) no <head> para SEO / LLMs
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(SITE_JSONLD);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const quote = TESTIMONIALS[quoteIdx];
  const goSignup = () => navigate('/cadastrar-igreja');
  const goLogin = () => navigate('/login');

  return (
    <>
      <style>{LANDING_CSS}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      {/* ============ HEADER ============ */}
      <header>
        <div className="container nav">
          <a className="brand" href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src={ASSET('logo-conectachurch-white.svg')} alt="ConectaChurch" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
          <nav className="nav-links">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}>{l.label}</a>
            ))}
          </nav>
          <div className="nav-cta">
            <a className="btn btn-lime" href="#contato" onClick={(e) => { e.preventDefault(); setContactModal(true); }} style={{ fontSize: '0.8rem', padding: '8px 16px' }}>
              Agendar demonstração <span className="arr">→</span>
            </a>
            <button className="burger" aria-label="Abrir menu" onClick={() => setMenuOpen(true)}><span /><span /><span /></button>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="hero" id="top">
        <div className="hero-glow"></div>
        <video className="hero-video" autoPlay muted loop playsInline poster={ASSET('dashboard-backdrop.jpg')}>
          <source src={ASSET('hero.mp4')} type="video/mp4" />
        </video>
        <div className="container">
          <div className="hero-grid">
            <div className="hero-copy">
              <span className="eyebrow">Plataforma completa</span>
              <h1>A gestão da sua igreja. <span className="t">Conectada.</span></h1>
              <p className="sub">Consolidamos a gestão da sua comunidade em uma única plataforma — do primeiro contato ao discipulado profundo.</p>
              <div className="hero-actions">
                <button className="btn btn-lime" onClick={() => setContactModal(true)}>Agendar demonstração <span className="arr">→</span></button>
                <a className="btn btn-ghost" href="#plataforma" onClick={(e) => { e.preventDefault(); scrollTo('#plataforma'); }}>Ver modelo</a>
              </div>
              <div className="social">
                <div className="avatars">
                  <img src={ASSET('avatar-1.jpg')} alt="Membro" />
                  <img src={ASSET('avatar-2.jpg')} alt="Membro" />
                  <img src={ASSET('avatar-3.jpg')} alt="Membro" />
                </div>
                <small>Mais de 500 igrejas já usam o ConectaChurch para cuidar do que importa.</small>
              </div>
            </div>

            <div className="stats-network">
              <svg className="stats-svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <g stroke="rgba(0,241,167,0.18)" strokeWidth="0.4" fill="none">
                  <line x1="30" y1="30" x2="70" y2="30" />
                  <line x1="30" y1="30" x2="30" y2="70" />
                  <line x1="70" y1="30" x2="70" y2="70" />
                  <line x1="30" y1="70" x2="70" y2="70" />
                  <line x1="50" y1="15" x2="50" y2="50" />
                </g>
              </svg>
              <div className="stats-grid">
                <div className="stat">
                  <div className="val">600<span>+</span></div>
                  <div className="lbl">Igrejas conectadas</div>
                  <div className="ic">◆</div>
                </div>
                <div className="stat">
                  <div className="val">98<span>%</span></div>
                  <div className="lbl">Satisfação</div>
                  <div className="ic">◆</div>
                </div>
                <div className="stat">
                  <div className="val">100<span>%</span></div>
                  <div className="lbl">Dados seguros</div>
                  <div className="ic">◆</div>
                </div>
                <div className="stat">
                  <div className="val">76<span>%</span></div>
                  <div className="lbl">Mais tempo livre</div>
                  <div className="ic">◆</div>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="dash-wrap">
            <div className="dash-glow"></div>
            <div className="dash">
              <div className="dash-side">
                <div className="ds-head">
                  <span className="dot">◆</span>
                  <img src={ASSET('logo-conectachurch-white.svg')} alt="" />
                </div>
                <div className="ds-item active"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h18M3 6h18M3 18h18" /></svg> Visão geral</div>
                <div className="ds-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg> Membros</div>
                <div className="ds-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /></svg> Agenda</div>
                <div className="ds-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /></svg> Ministérios</div>
                <div className="ds-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 100 20z" /></svg> Células</div>
                <div className="ds-item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19h16M6 16l4-4 3 3 5-6" /></svg> Relatórios</div>
              </div>
              <div className="dash-main">
                <div className="ds-title">Visão geral</div>
                <div className="ds-kpis">
                  <div className="ds-kpi"><div className="l">Membros</div><div className="v">2.483</div><div className="d up">↑ 12% este mês</div></div>
                  <div className="ds-kpi"><div className="l">Inscritos</div><div className="v">119</div><div className="d up">↑ 8%</div></div>
                  <div className="ds-kpi"><div className="l">Ministérios</div><div className="v">33</div><div className="d lime">7 ativos</div></div>
                  <div className="ds-kpi"><div className="l">Células</div><div className="v">49</div><div className="d up">↑ 5</div></div>
                </div>
                <div className="ds-cols">
                  <div className="ds-card">
                    <h4>Adicionando membros</h4>
                    <div className="ds-member"><img src={ASSET('avatar-1.jpg')} alt="" loading="lazy" decoding="async" width="40" height="40" /><div><div className="n">Marina Souza</div><div className="r">Conciliação</div></div></div>
                    <div className="ds-member"><img src={ASSET('avatar-2.jpg')} alt="" loading="lazy" decoding="async" width="40" height="40" /><div><div className="n">Carlos Lima</div><div className="r">Jornada</div></div></div>
                    <div className="ds-member"><img src={ASSET('avatar-3.jpg')} alt="" loading="lazy" decoding="async" width="40" height="40" /><div><div className="n">Laura Martins</div><div className="r">Células</div></div></div>
                  </div>
                  <div className="ds-card">
                    <h4>Ganho de tempo</h4>
                    <div className="ds-chart">
                      <div className="bar" style={{ height: '38%' }}></div>
                      <div className="bar" style={{ height: '52%' }}></div>
                      <div className="bar" style={{ height: '44%' }}></div>
                      <div className="bar" style={{ height: '68%' }}></div>
                      <div className="bar" style={{ height: '58%' }}></div>
                      <div className="bar" style={{ height: '82%' }}></div>
                      <div className="bar" style={{ height: '72%' }}></div>
                      <div className="bar" style={{ height: '95%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="section features" id="plataforma">
        <div className="container">
          <div className="sec-head">
            <span className="eyebrow">Tudo que sua igreja precisa</span>
            <h2>Uma plataforma completa para cada necessidade</h2>
            <p>Sem precisar misturar planilha, WhatsApp e papel. O ConectaChurch centraliza a gestão pastoral da sua comunidade.</p>
          </div>
          <div className="feat-grid">
            {FEATURES.map((f) => (
              <div className="feat" key={f.title}>
                <div className="fic">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><path d={ICON_PATHS[f.icon]} />
                  </svg>
                </div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
                <a className="more" href="#contato" onClick={(e) => { e.preventDefault(); setContactModal(true); }}>Saiba mais <span className="arr">→</span></a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="section" id="como-funciona">
        <div className="container">
          <div className="sec-head">
            <span className="eyebrow">Como funciona</span>
            <h2>Comece em 3 passos simples</h2>
            <p>Da importação dos membros aos relatórios prontos — em poucos minutos sua igreja está organizada.</p>
          </div>
          <div className="steps-grid">
            {STEPS.map((s) => (
              <div className="step" key={s.num}>
                <div className="num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
            <svg className="steps-arrow s1" width="60" height="40" viewBox="0 0 60 40" fill="none" stroke="currentColor" strokeWidth="2"><path d="M0 20h48M48 20l-8-8M48 20l-8 8" /></svg>
            <svg className="steps-arrow s2" width="60" height="40" viewBox="0 0 60 40" fill="none" stroke="currentColor" strokeWidth="2"><path d="M0 20h48M48 20l-8-8M48 20l-8 8" /></svg>
          </div>
          <div className="steps-foot">
            <button className="btn btn-lime" onClick={() => setContactModal(true)}>Começar agora <span className="arr">→</span></button>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIAL ============ */}
      <section className="section testimonial">
        <div className="container">
          <div className="testi-grid">
            <div className="testi-copy">
              <span className="eyebrow">Seus resultados viram história</span>
              <h2 style={{ fontSize: 'clamp(1.9rem,3.2vw,2.8rem)', fontWeight: 800 }}>Tecnologia para fortalecer relacionamentos</h2>
              <ul className="checks">
                {CHURCH_BENEFITS.map((b) => (
                  <li key={b}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>
                    {b}
                  </li>
                ))}
              </ul>
              <div className="hero-actions" style={{ marginTop: 32 }}>
                <a className="btn btn-ghost" href="#contato" onClick={(e) => { e.preventDefault(); setContactModal(true); }}>Conhecer a plataforma <span className="arr">→</span></a>
              </div>
            </div>
            <div className="quote-card">
              <div className="qmark">“</div>
              <blockquote>{quote.quote}</blockquote>
              <div className="quote-person">
                <img src={ASSET('pastor-rafael.jpg')} alt={quote.name} loading="lazy" decoding="async" width="80" height="80" />
                <div>
                  <div className="n">{quote.name}</div>
                  <div className="r">{quote.role}</div>
                </div>
              </div>
              <div className="quote-nav">
                <button className="qbtn" aria-label="Anterior" onClick={() => setQuoteIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}>←</button>
                <button className="qbtn" aria-label="Próximo" onClick={() => setQuoteIdx((i) => (i + 1) % TESTIMONIALS.length)}>→</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="section" id="planos">
        <div className="container">
          <div className="sec-head">
            <span className="eyebrow">Planos que cabem no seu bolso</span>
            <h2>Escolha o plano ideal e cresça junto com sua comunidade</h2>
            <p>Comece grátis por 7 dias. Sem cartão de crédito.</p>
          </div>
          <div className="price-grid">
            {planos.length === 0 ?
              PLANS.map((p) => (
                <div className={`plan${p.featured ? ' featured' : ''}`} key={p.id}>
                  <div className="tag">{p.tag}</div>
                  <h3>{p.title}</h3>
                  <div className="price">{p.price}<small>{p.suffix}</small></div>
                  <p className="desc">{p.desc}</p>
                  <ul>
                    {p.features.map((f) => (
                      <li key={f}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg> {f}</li>
                    ))}
                  </ul>
                  <button className={`btn ${p.featured ? 'btn-lime' : 'btn-ghost'}`} onClick={() => goSignup()}>
                    {p.cta}{p.featured ? <span className="arr"> →</span> : null}
                  </button>
                </div>
              ))
              :
              planos.map((plano) => {
                const destaque = plano.slug === 'igreja';
                const modulos = planModulos(plano);
                return (
                  <div className={`plan${destaque ? ' featured' : ''}`} key={plano.id}>
                    <div className="tag">{planTag(plano)}</div>
                    <h3>{plano.nome}</h3>
                    <div className="price">{formatPrice(plano.preco_mensal)}<small>/mês</small></div>
                    <p className="desc">{plano.descricao || ''}</p>
                    <ul>
                      {modulos.map((f) => (
                        <li key={f}><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg> {f}</li>
                      ))}
                    </ul>
                    <button className={`btn ${destaque ? 'btn-lime' : 'btn-ghost'}`} onClick={() => navigate(`/cadastrar-igreja?plano=${plano.id}`)}>
                      {plano.preco_mensal === 0 ? 'Testar grátis' : 'Começar agora'}{destaque ? <span className="arr"> →</span> : null}
                    </button>
                    {plano.trial_dias > 0 ? (
                      <div className="trial">{plano.trial_dias} dias de teste gratuito</div>
                    ) : null}
                  </div>
                );
              })
            }
          </div>
        </div>
      </section>

      {/* ============ CTA BAND ============ */}
      <section className="section" id="contato" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="cta-band">
            <div className="cta-inner">
              <div>
                <h2>Sua igreja já está <span>conectada?</span></h2>
                <p className="sub">Agende uma demonstração gratuita de 30 minutos. Sem compromisso — online.</p>
                <div className="cta-actions">
                  <button className="btn btn-lime" onClick={() => setContactModal(true)}>Agendar demonstração <span className="arr">→</span></button>
                  <a className="btn btn-ghost" href="#plataforma" onClick={(e) => { e.preventDefault(); scrollTo('#plataforma'); }}>Ver funcionalidades</a>
                </div>
              </div>
              <div className="brand" aria-hidden="true">
                <img src={ASSET('logo-conectachurch-white.svg')} alt="" style={{ height: 80 }} loading="lazy" decoding="async" width="200" height="80" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="container">
          <div className="foot-grid">
            <div className="foot-brand">
              <a className="brand" href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <img src={ASSET('logo-conectachurch-white.svg')} alt="ConectaChurch" style={{ height: 48 }} loading="lazy" decoding="async" width="160" height="48" />
              </a>
              <p>Plataforma completa para a gestão de igrejas: membros, células, ministérios, finanças e comunicação em um só lugar.</p>
            </div>
            <div className="foot-col">
              <h4>Plataforma</h4>
              <ul>
                <li><a href="#plataforma" onClick={(e) => { e.preventDefault(); scrollTo('#plataforma'); }}>Produtos</a></li>
                <li><a href="#como-funciona" onClick={(e) => { e.preventDefault(); scrollTo('#como-funciona'); }}>Como funciona</a></li>
                <li><a href="#planos" onClick={(e) => { e.preventDefault(); scrollTo('#planos'); }}>Para igrejas</a></li>
                <li><a href="#contato" onClick={(e) => { e.preventDefault(); scrollTo('#contato'); }}>Contato</a></li>
              </ul>
            </div>
            <div className="foot-col">
              <h4>Recursos</h4>
              <ul>
                <li><a href="/recursos">Recursos</a></li>
                <li><a href="/quem-somos">Sobre nós</a></li>
                <li><a href="/regras-teste">Central de ajuda</a></li>
                <li><a href="mailto:contato@conectachurch.com.br">Suporte</a></li>
              </ul>
            </div>
            <div className="foot-col">
              <h4>Empresa</h4>
              <ul>
                <li><a href="/login" onClick={(e) => { e.preventDefault(); goLogin(); }}>Acessar sistema</a></li>
                <li><a href="/cadastrar-igreja" onClick={(e) => { e.preventDefault(); goSignup(); }}>Criar conta</a></li>
                <li><a href="/termos">Termos</a></li>
                <li><a href="/privacidade">Privacidade</a></li>
              </ul>
            </div>
          </div>
          <div className="foot-bottom">
            <small>© {new Date().getFullYear()} ConectaChurch. Todos os direitos reservados.</small>
            <small>Feito para ajudar igrejas a cuidar de pessoas.</small>
          </div>
        </div>
      </footer>

      {/* ============ MOBILE MENU ============ */}
      <Drawer anchor="right" open={menuOpen} onClose={() => setMenuOpen(false)} PaperProps={{ sx: { width: 280, bgcolor: '#0A141C', color: TEXT_LIGHT } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <img src={ASSET('logo-conectachurch-white.svg')} alt="ConectaChurch" style={{ height: 42 }} />
          <IconButton onClick={() => setMenuOpen(false)} sx={{ color: TEXT_LIGHT }}><CloseIcon /></IconButton>
        </Box>
        <List sx={{ flex: 1, pt: 2 }}>
          {NAV_LINKS.map((l) => (
            <ListItem key={l.label} disablePadding>
              <ListItemButton onClick={() => scrollTo(l.href)} sx={{ py: 1.5, color: TEXT_LIGHT, '&:hover': { bgcolor: 'rgba(0,241,167,0.08)' } }}>
                <ListItemText primary={l.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button fullWidth variant="contained" onClick={() => { setMenuOpen(false); setContactModal(true); }} sx={{ bgcolor: '#B4F300', color: '#0B1200', fontWeight: 700 }}>
            Agendar demonstração
          </Button>
        </Box>
      </Drawer>

      <ContactModal open={contactModal} onClose={() => setContactModal(false)} />
    </>
  );
}
